import { NullableResultPromise } from "../base-types";
import { IExtendedContentHubClient } from "../clients/extended-client";
import { IMemberGroup, MemberGroup } from "../contracts/base/member-group";
import Guard from "../guard";
import { MemberGroupResource } from "../models/member-group-resource";
import { MemberConditionMapper } from "./member-condition-mapper";
import { MemberDefinitionMapper } from "./member-definition-mapper";

export class MemberGroupMapper {
    private readonly _client: IExtendedContentHubClient;
    private readonly _memberDefinitionMapper: MemberDefinitionMapper;
    private readonly _memberConditionMapper: MemberConditionMapper;

    constructor(client: IExtendedContentHubClient) {
        Guard.notNullOrUndefined(client, "client");

        this._client = client;
        this._memberDefinitionMapper = new MemberDefinitionMapper(client);
        this._memberConditionMapper = new MemberConditionMapper(client);
    }

    //#region To SDK
    async mapMemberGroupsAsync(groupResources: Array<MemberGroupResource>): Promise<Array<IMemberGroup>> {
        if (!groupResources || groupResources.length === 0) return [];

        const list: Array<IMemberGroup> = [];
        for (const resource of groupResources) {
            const group = await this.mapMemberGroupAsync(resource);
            if (group != null) {
                list.push(group);
            }
        }

        return list;
    }

    async mapMemberGroupAsync(groupResource: MemberGroupResource): NullableResultPromise<IMemberGroup> {
        if (!groupResource) return null;

        const memberGroup = new MemberGroup({
            name: groupResource.name,
            isSecured: groupResource.isSecured || false,
            isSystemOwned: groupResource.isSystemOwned,
            contentGroup: groupResource.contentGroup,
            conditions: await this._memberConditionMapper.mapMemberConditionsAsync(groupResource.conditions),
            memberDefinitions: await this._memberDefinitionMapper.mapMemberDefinitionsAsync(
                groupResource.memberDefinitions
            ),
            labels: JSON.parse(JSON.stringify(groupResource.labels)),
        });

        return memberGroup;
    }
    //#endregion

    //#region To API resource
    async mapMemberGroupResourcesAsync(memberGroups: Array<IMemberGroup>): Promise<Array<MemberGroupResource>> {
        if (!memberGroups || memberGroups.length === 0) return [];

        const list: Array<MemberGroupResource> = [];

        for (const memberGroup of memberGroups) {
            const resource = await this.mapMemberGroupResourceAsync(memberGroup);
            if (resource) {
                list.push(resource);
            }
        }

        return list;
    }

    async mapMemberGroupResourceAsync(memberGroup: IMemberGroup): NullableResultPromise<MemberGroupResource> {
        if (memberGroup == null) return null;

        const conditions = await this._memberConditionMapper.mapMemberConditionResourcesAsync(memberGroup.conditions);
        const memberDefinitions = await this._memberDefinitionMapper.mapMemberDefinitionResourcesAsync(
            memberGroup.memberDefinitions
        );

        const resource = new MemberGroupResource({
            name: memberGroup.name,
            isSecured: memberGroup.isSecured != null ? memberGroup.isSecured : false,
            isSystemOwned: memberGroup.isSystemOwned != null ? memberGroup.isSystemOwned : false,
            contentGroup: memberGroup.contentGroup,
            labels: JSON.parse(JSON.stringify(memberGroup.labels || {})),
            conditions: conditions,
            isConditional: memberGroup.isConditional,
            memberDefinitions: memberDefinitions,
        });

        return resource;
    }
    //#endregion
}
